import { useEffect, useState } from "react";
import MultiPageSketchPad from "@/components/sketch/MultiPageSketchPad";

/**
 * Modal wrapper around DrivingSketchPad used from InstructorDashboard.
 * Keeps title/description metadata separate from the drawing surface so the
 * pad itself stays reusable for read-only learner display too.
 */
export default function SketchModal({
  open,
  onClose,
  onSave,
  saving = false,
  initialTitle = "",
  initialDescription = "",
  initialTemplateId = "blank",
  initialStrokesJson = "",
  initialPages = null,
  error = "",
  heading = "Driving sketch",
  readOnly = false,
}) {
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [templateId, setTemplateId] = useState(initialTemplateId);

  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setDescription(initialDescription);
    setTemplateId(initialTemplateId);
  }, [open, initialTitle, initialDescription, initialTemplateId]);

  if (!open) return null;

  const handleSave = (payload) => {
    onSave?.({
      title,
      description,
      template_id: payload?.template_id || templateId,
      strokes_json: payload?.strokes_json || JSON.stringify([]),
      pages: payload?.pages,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      data-testid="sketch-modal"
    >
      <div className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{heading}</h2>
          <button
            type="button"
            className="btn-outline min-h-11"
            onClick={onClose}
            aria-label="Close sketch editor"
            data-testid="sketch-modal-close"
          >
            Close
          </button>
        </div>

        {!readOnly && (
          <>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Title</span>
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                placeholder="e.g. Roundabout gap selection"
                data-testid="sketch-title"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm font-medium">Description (optional)</span>
              <textarea
                className="input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={2000}
                data-testid="sketch-description"
              />
            </label>
          </>
        )}

        <MultiPageSketchPad
          initialPages={initialPages}
          initialStrokesJson={initialStrokesJson}
          initialTemplateId={templateId}
          onSave={handleSave}
          saving={saving}
          readOnly={readOnly}
        />

        {error && (
          <p role="alert" className="text-destructive" data-testid="sketch-modal-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
